/**
 * Blocks editor — chip token model + (de)serialization helpers
 * For Media Devoted | Created By Ciro Autiero
 *
 * The Blocks editor renders the formula as a left-to-right list of clickable
 * chips. The chip list IS the source of truth in the editor; on every change
 * we serialize chips back to canonical text and re-parse into the persisted
 * FormulaBlockNode AST. Anything the parser rejects keeps the chips visible
 * but produces a `null` AST so the parent surfaces an "invalid" status.
 */

import { isSubformulaRefName, tokenize } from '@trafficpulse/shared';
import type { Token } from '@trafficpulse/shared';

export type ChipKind =
  | 'metric'
  | 'subref'
  | 'number'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'fn';

export interface ChipToken {
  id: string;
  kind: ChipKind;
  value: string;
}

const FUNCTION_NAMES: ReadonlySet<string> = new Set([
  'min',
  'max',
  'abs',
  'log',
  'ln',
  'sqrt',
  'lowest',
]);

let counter = 0;
const nextId = (): string => `c_${Date.now().toString(36)}_${counter++}`;

export function makeChip(kind: ChipKind, value: string): ChipToken {
  return { id: nextId(), kind, value };
}

export function chipsToText(chips: readonly ChipToken[]): string {
  return chips.map((c) => c.value).join(' ');
}

export function textToChips(text: string): ChipToken[] {
  if (!text.trim()) return [];
  let tokens: Token[];
  try {
    tokens = tokenize(text);
  } catch {
    return [];
  }
  const chips: ChipToken[] = [];
  for (const token of tokens) {
    const chip = tokenToChip(token);
    if (chip) chips.push(chip);
  }
  return chips;
}

function tokenToChip(token: Token): ChipToken | null {
  if (token.type === 'eof') return null;
  if (token.type === 'number') return makeChip('number', token.value);
  if (token.type === 'ident') return identToChip(token.value);
  if (token.type === 'lparen') return makeChip('lparen', '(');
  if (token.type === 'rparen') return makeChip('rparen', ')');
  if (token.type === 'comma') return makeChip('comma', ',');
  return makeChip('op', token.value);
}

function identToChip(value: string): ChipToken {
  if (FUNCTION_NAMES.has(value)) return makeChip('fn', value);
  if (isSubformulaRefName(value)) return makeChip('subref', value);
  return makeChip('metric', value);
}
